import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, BarChart3, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import ScopePicker, { useScopeOptions } from "@/components/ScopePicker";
import { Chip } from "./shared";
import PerformanceView from "./PerformanceView";
import BootcampView from "./BootcampView";
import StarsView from "./StarsView";
import StpTeamView from "@/components/stp/StpTeamView";

const VIEWS = [["performance", "Performance", PerformanceView], ["bootcamp", "Bootcamp Progress", BootcampView],
  ["stars", "Rising Stars & Eagles", StarsView], ["stp", "STP Run", StpTeamView]];

export default function TeamReportCard() {
  const { user, isViewAs, startViewAs } = useAuth();
  const [view, setView] = useState("performance");
  const [q, setQ] = useState("");
  const { options } = useScopeOptions();
  const scopeOpts = useMemo(() => options.filter((o) => o.value !== "personal"), [options]);
  const [scope, setScope] = useState("team");
  const viewAs = async (u) => {
    if (await startViewAs(u.id)) window.location.href = "/";
    else toast.error(`Unable to View As ${u.name}`);
  };
  const View = VIEWS.find(([k]) => k === view)[2];
  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="team-users-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0A1F44] text-[#C9A961] flex items-center justify-center shrink-0"><Users className="w-5 h-5" /></div>
          <div>
            <h2 className="font-serif text-lg text-[#0A1F44]">Team Users</h2>
            <p className="text-xs text-slate-500">{user?.role === "admin" ? "Everyone in your selected group" : user?.role === "oversight" ? "Everyone in ATL" : "People in your branch"} · tap the eye for a read-only preview</p>
            <Link to="/milestones" data-testid="team-users-report-link" className="inline-flex items-center gap-1 text-xs font-medium text-[#0A1F44] hover:text-[#8A6D1F] mt-1">
              <BarChart3 className="w-3.5 h-3.5" /> Milestone & qualification report
            </Link>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {scopeOpts.length > 1 && <ScopePicker value={scope} onChange={setScope} options={scopeOpts} testid="team-report-scope" />}
          <div className="relative w-full sm:w-60">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name" className="pl-9 h-9 text-sm" data-testid="team-users-search" />
          </div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" data-testid="team-report-views">
        {VIEWS.map(([k, label]) => <Chip key={k} active={view === k} onClick={() => setView(k)} testid={`team-view-${k}`}>{label}</Chip>)}
      </div>
      <View scope={scope} q={q} rowProps={{ canViewAs: !isViewAs, onViewAs: viewAs }} />
    </Card>
  );
}
